import { isMapId, teleportCommand } from "@69ly/maps";
import type { Stuff } from "@69ly/shared";
import { useTranslation } from "react-i18next";
import { Link } from "react-router";
import { useCallouts } from "../lib/callouts.ts";
import { useStuff } from "../lib/stuff-queries.ts";
import { CopyButton } from "./CopyButton.tsx";
import { CopyToMenu } from "./CopyTo.tsx";
import { GrenadeIcon } from "./MarkerIcon.tsx";
import { Technique } from "./Technique.tsx";
import { Icon } from "./Icon.tsx";
import { StatusControl } from "./StatusControl.tsx";
import { MediaViewer } from "./StuffMedia.tsx";
import { UsedInList } from "./UsedIn.tsx";
import { WindowsBar } from "./WindowsBar.tsx";
import { ImportanceTag } from "./Importance.tsx";

/**
 * Everything about a stuff, sized for a side panel or the drawer: media by kind,
 * how to throw it, when, notes, my status, where it is used, and the actions.
 * Nothing needs the full page.
 */
export function StuffSheet({ stuff: given, titleId }: { stuff: Stuff; titleId?: string }) {
  const { t } = useTranslation();
  const callouts = useCallouts();
  // Lists carry most of it; "used in" comes with the stuff's own request.
  const full = useStuff(given.id);
  const stuff = full.data ?? given;
  const places = isMapId(stuff.map) ? callouts.places(stuff.map, stuff) : { from: null, to: null };

  return (
    <div className="stuff-sheet">
      <header className="sheet-head">
        <GrenadeIcon grenade={stuff.grenade} side={stuff.side} size={34} />
        <div className="sheet-title">
          <h2 className="panel-title" id={titleId}>
            {stuff.title}
          </h2>
          <p className="muted small">
            <span className={`side-tag side-${stuff.side}`}>{t(`sideShort.${stuff.side}`)}</span> <ImportanceTag importance={stuff.importance} /> {t(`grenade.${stuff.grenade}`)} · {t(`area.${stuff.area}`)} ·{" "}
            {t("mapView.by", { name: stuff.author.name })}
          </p>
          {(places.from || places.to) && (
            <p className="small sheet-places">
              {places.from && (
                <>
                  {t("places.thrownFrom")} <b>{places.from}</b>
                </>
              )}
              {places.from && places.to && " → "}
              {places.to && (
                <>
                  {t(places.from ? "places.landsOn" : "places.landsOnAlone")} <b>{places.to}</b>
                </>
              )}
            </p>
          )}
        </div>
      </header>

      <div className="sheet-actions">
        {stuff.canEdit && (
          <Link to={`/stuffs/${stuff.id}/edit`} className="btn btn-secondary btn-small">
            {t("mapView.edit")}
          </Link>
        )}
        <CopyToMenu stuffId={stuff.id} from={stuff.teamId ?? "personal"} small />
        <Link to={`/stuffs/${stuff.id}`} className="btn btn-quiet btn-small" title={t("sheet.fullPage")}>
          <Icon name="external" size={14} />
          {t("sheet.fullPage")}
        </Link>
      </div>

      <MediaViewer stuff={stuff} empty={t("detail.noPictures")} />

      <section className="sheet-section">
        <h3 className="eyebrow">{t("sheet.how")}</h3>
        <Technique technique={stuff.technique} />
        <div className="command">
          <code>{teleportCommand(stuff.throw)}</code>
          <CopyButton text={teleportCommand(stuff.throw)} />
        </div>
        <p className="muted small">{t("mapView.teleportHelp")}</p>
      </section>

      {stuff.windows.length > 0 && (
        <section className="sheet-section">
          <h3 className="eyebrow">{t("mapView.windows")}</h3>
          <WindowsBar windows={stuff.windows} />
        </section>
      )}

      {stuff.notes && (
        <section className="sheet-section">
          <h3 className="eyebrow">{t("detail.notes")}</h3>
          <p className="notes">{stuff.notes}</p>
        </section>
      )}

      <section className="sheet-section">
        <h3 className="eyebrow">{t("mapView.myStatus")}</h3>
        <StatusControl stuffId={stuff.id} status={stuff.myStatus} />
        <p className="muted small">
          {stuff.teamId ? t("mapView.teamProgress", { learned: stuff.progress.learned, members: stuff.progress.members }) : t("mapView.personalProgress")}
        </p>
      </section>

      {(stuff.usedIn?.length ?? 0) > 0 && (
        <section className="sheet-section">
          <h3 className="eyebrow">{t("usedIn.title")}</h3>
          <UsedInList stuff={stuff} />
        </section>
      )}
    </div>
  );
}
